import type { ReactNode } from "react";

import type { PaymentState, PaymentInfo } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { AlertTriangle, LinkForward } from "@/components/icons";

// ── Shared payment-status presentation ─────────────────────────────────────────
// The SINGLE source of truth for how a session's payment state is labelled and
// rendered, so every surface (the admin org sessions report AND the per-charger
// Session History) uses IDENTICAL vocabulary. Values are PROJECTIONS of the
// backend's factual payment_state (organizations/session_report.classify_payment)
// — nothing here decides payment.

type BadgeVariant = "success" | "neutral" | "warning" | "error" | "info";

// The payment-facts contract (PaymentInfo) is defined in lib/api.ts — the API
// model owns the backend response shape; this presentation module only renders it.
// OrgSessionReportRow is structurally assignable to PaymentInfo, and the
// per-charger sessions API attaches the same shape as `session.payment`.

// Maps the backend factual payment_state to a badge label + variant. Labels are
// deliberately factual: `Authorized` and `Not captured` are shown only where that
// is what the persisted state actually is; a bare authorization hold with an unknown
// final bill reads as "Billing not finalized", never "not captured".
export const PAYMENT_STATE_META: Record<
  PaymentState,
  { label: string; variant: BadgeVariant }
> = {
  paid: { label: "Paid", variant: "success" },
  free: { label: "Free", variant: "neutral" },
  billing_processing: { label: "Billing processing", variant: "info" },
  billing_stuck: { label: "Billing stuck", variant: "error" },
  captured_amount_unverified: { label: "Captured · final amount unknown", variant: "warning" },
  authorized_awaiting_capture: { label: "Authorized · not captured", variant: "warning" },
  capture_failed: { label: "Capture failed", variant: "error" },
  partially_captured: { label: "Partially captured", variant: "error" },
  customer_action_required: { label: "Customer action needed", variant: "warning" },
  payment_locked: { label: "Payment locked", variant: "error" },
  canceled: { label: "Canceled", variant: "neutral" },
  abandoned: { label: "Abandoned", variant: "warning" },
  billing_not_finalized: { label: "Billing not finalized", variant: "warning" },
  billing_error: { label: "Billing error", variant: "error" },
  missing_payment_intent: { label: "No payment intent", variant: "error" },
  data_mismatch: { label: "Data mismatch", variant: "error" },
  // Migrated (Energic) sessions: billed under a real tariff but nothing captured.
  // "Uncollected" = revenue never collected (with the root cause); "Free · charging
  // schedule" = intentionally waived under a Free Charging Schedule. Terminal, not actionable.
  uncollected_no_payment_method: { label: "Uncollected · no payment method", variant: "warning" },
  uncollected_charger_offline: { label: "Uncollected · charger offline", variant: "warning" },
  uncollected_payment_failed: { label: "Uncollected · payment failed", variant: "warning" },
  uncollected_authorized_not_captured: { label: "Uncollected · not captured", variant: "warning" },
  free_charging_schedule: { label: "Free · charging schedule", variant: "neutral" },
  unknown: { label: "Unknown", variant: "neutral" },
};

// Human-readable attention_reason labels (machine value stays in the CSV / API).
export const ATTENTION_REASON_LABEL: Record<string, string> = {
  authorization_not_captured: "Card authorized but never captured",
  capture_failed: "Capture attempt failed",
  partial_capture: "Only part of the bill was captured",
  customer_action_required: "Customer must provide a payment method",
  payment_locked: "Locked after repeated declines",
  closed_unpaid_balance: "Session closed with an unpaid balance",
  missing_payment_intent: "Amount due but no payment intent on file",
  missing_finalized_invoice: "Captured, but no finalized invoice to verify against",
  billing_not_finalized: "Final charge was never finalized",
  billing_failed: "Billing finalization failed",
  billing_failed_with_captured_amount:
    "Billing failed but money was captured — reconcile the payment",
  billing_stuck: "No billing progress within the alert window",
  billing_error: "Billing computation failed",
  status_amount_mismatch: "Status and amounts disagree",
  unknown_status: "Unrecognized payment status while money is due",
  // Migrated uncollected / free-by-grant reasons.
  no_payment_method_authorized: "Charged without a payment method authorized — nothing collected",
  charger_offline_no_authorization: "Charger was offline — payment could not be authorized",
  payment_failed: "Payment failed — nothing collected",
  authorized_not_captured: "Authorized but the charge was never captured",
  free_charging_schedule: "Free under a Free Charging Schedule (billed amount waived)",
};

export function paymentStateMeta(state: PaymentState): { label: string; variant: BadgeVariant } {
  return PAYMENT_STATE_META[state] ?? PAYMENT_STATE_META.unknown;
}

function money(v: number | null | undefined): string {
  if (v === null || v === undefined) return "—";
  return `$${v.toFixed(2)}`;
}

// Build the Stripe dashboard deep-link for a payment intent (mode-agnostic — the
// operator can switch test/live in Stripe). Only rendered when an intent exists.
function stripePaymentUrl(intentId: string): string {
  return `https://dashboard.stripe.com/payments/${encodeURIComponent(intentId)}`;
}

/** The status badge on its own (no tooltip) — for compact / non-table surfaces. */
export function PaymentBadge({
  state,
  needsAttention = false,
  className,
}: {
  state: PaymentState;
  needsAttention?: boolean;
  className?: string;
}) {
  const meta = paymentStateMeta(state);
  return (
    <Badge variant={meta.variant} className={`cursor-default gap-1 ${className ?? ""}`}>
      {needsAttention && <AlertTriangle className="h-3 w-3" />}
      {meta.label}
    </Badge>
  );
}

/** One "label: value" line inside the payment diagnostics tooltip. */
function DiagLine({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="opacity-70">{label}</span>
      <span className="font-medium">{value}</span>
    </div>
  );
}

/**
 * The Payment cell: a factual status badge. For flagged rows the badge carries a
 * hover tooltip with the persisted diagnostics an operator needs — amount due (or
 * "not finalized"), captured, CONFIRMED outstanding (only when the amount is known),
 * the authorization hold shown separately as diagnostic (never as money owed), any
 * decline / billing-error detail, and — when `showStripeLink` — a deep-link to the
 * Stripe payment intent (kept to the admin org report; hidden on the per-charger view).
 */
export function PaymentCell({
  r,
  showStripeLink = true,
}: {
  r: PaymentInfo;
  showStripeLink?: boolean;
}) {
  const meta = paymentStateMeta(r.payment_state);
  const badge = <PaymentBadge state={r.payment_state} needsAttention={r.payment_needs_attention} />;

  const hasDiagnostics =
    r.payment_needs_attention ||
    (showStripeLink && !!r.payment_intent_id) ||
    !!r.payment_last_decline_code ||
    !!r.billing_error ||
    r.payment_state === "unknown";

  if (!hasDiagnostics) {
    return <span title={r.payment_status ?? ""}>{badge}</span>;
  }

  const reasonLabel = r.attention_reason
    ? ATTENTION_REASON_LABEL[r.attention_reason] ?? r.attention_reason
    : null;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{badge}</TooltipTrigger>
      <TooltipContent className="max-w-[21rem] flex-col items-stretch gap-1 py-2 text-left">
        <div className="font-semibold">{meta.label}</div>
        {reasonLabel && <div className="mb-0.5 opacity-80">{reasonLabel}</div>}
        <DiagLine label="Status" value={r.payment_status || "—"} />
        <DiagLine
          label="Amount due"
          value={r.amount_due != null ? money(r.amount_due) : "not finalized"}
        />
        <DiagLine label="Captured" value={money(r.captured_payment)} />
        <DiagLine
          label="Outstanding"
          value={
            r.outstanding_known
              ? money(r.outstanding_amount)
              : "unknown (final charge not finalized)"
          }
        />
        {/* The authorization hold is a temporary Stripe pre-auth, NOT an amount owed. */}
        {r.authorized_hold_amount != null && (
          <DiagLine label="Auth hold (diagnostic)" value={money(r.authorized_hold_amount)} />
        )}
        {r.billing_error && <DiagLine label="Billing error" value={r.billing_error} />}
        {r.payment_last_decline_code && (
          <DiagLine
            label="Last decline"
            value={
              r.payment_last_decline_at
                ? `${r.payment_last_decline_code} · ${r.payment_last_decline_at}`
                : r.payment_last_decline_code
            }
          />
        )}
        {(r.payment_hard_decline_count ?? 0) > 0 && (
          <DiagLine label="Hard declines" value={r.payment_hard_decline_count} />
        )}
        {showStripeLink &&
          (r.payment_intent_id ? (
            <a
              href={stripePaymentUrl(r.payment_intent_id)}
              target="_blank"
              rel="noreferrer"
              className="mt-1 inline-flex items-center gap-1 underline underline-offset-2 hover:opacity-80"
            >
              <LinkForward className="h-3 w-3" />
              Open in Stripe
            </a>
          ) : (
            <div className="mt-1 opacity-70">No payment intent on file</div>
          ))}
      </TooltipContent>
    </Tooltip>
  );
}
